/**
 * PageHeader — Shared premium page-level header component.
 *
 * Props:
 *  icon        {ReactNode}  MUI icon element to display inside the gradient box.
 *  title       {string}     Bold main heading text.
 *  subtitle    {string}     Muted description text below the title.
 *  gradient    {string}     CSS gradient string for the icon box background.
 *                           Defaults to indigo: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, ThemeProvider, createTheme } from '@mui/material';

export const pageHeaderTheme = createTheme({
  typography: {
    fontFamily: 'ProductSansLight, sans-serif',
    h3: {
      fontFamily: 'ProductSansBold, sans-serif',
      fontSize: 28,
      fontWeight: 600,
      color: '#1e293b',
    },
    subtitle1: {
      color: '#64748b',
      fontSize: 14,
    },
  },
});

const PageHeader = ({ icon, title, subtitle, gradient }) => (
  <ThemeProvider theme={pageHeaderTheme}>
    <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box
        sx={{
          p: 1.5,
          borderRadius: '12px',
          background: gradient || 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <div>
        <Typography variant="h3">{title}</Typography>
        {subtitle && (
          <Typography variant="subtitle1">{subtitle}</Typography>
        )}
      </div>
    </Box>
  </ThemeProvider>
);

PageHeader.propTypes = {
  icon:     PropTypes.node.isRequired,
  title:    PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  gradient: PropTypes.string,
};

export default PageHeader;
